import { useCallback, useMemo, useState, useSyncExternalStore } from "react";
import {
  notifyManager,
  partialMatchKey,
  skipToken,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";

import { catalogItemDetail, catalogItemsList } from "@/api/functions/catalog";
import { catalogManufacturersByIds } from "@/api/functions/catalog-admin";
import {
  CATALOG_ITEMS_STALE_TIME_MS,
  catalogItemDetailQueryKeyPrefix,
} from "@/hooks/catalog-items";
import { DEFAULT_CATALOG_SORT } from "@/lib/catalog-list-url-state";
import {
  islandManufacturerPicksQueryKey,
  type PickedManufacturer,
} from "@/hooks/item-search-island-manufacturers";
import { useLastAnswer } from "@/hooks/use-last-answer";
import { islandIdList, islandListInput, type IslandSearch } from "@/lib/item-search-island";

// The Item Search island's reads (#811): the donor's infinite search
// (`itemsApi.ts:260-322`, `searchItemsFalcon`) and its single-item read
// (`?itemId=`, `limit 1`), both over the catalog's list read.
//
// **Keyed apart from the Item Catalog page's lists**, so the catalog's writes
// — a request, a delete, an added variant — do not re-read the island, as the
// donor's search carries no tags and re-reads nothing after them. The pages a
// query has loaded are its cache, as the donor's merged `itemIds` are; the
// donor keys that cache on the query without its cursor, and so does this.

type ListPage = Awaited<ReturnType<typeof catalogItemsList>> & {
  /**
   * The URL's manufacturers read by id, retired ones included — falcon's
   * `include_deleted=true` read, made inside each search request's task group
   * and returned in the same response (`item_search_island/router.py:140-173`),
   * so the two answer or fail together (#964 N18). Absent with no `mfrs`.
   */
  manufacturersById?: { id: number; name: string }[];
};

/** One page of the island's search: the list read and, with `mfrs`, the by-id read. */
async function searchPage(search: IslandSearch, page: number): Promise<ListPage> {
  const ids = islandIdList(search.mfrs);
  const [list, manufacturers] = await Promise.all([
    catalogItemsList({ data: islandListInput(search, page) }),
    ids ? catalogManufacturersByIds({ data: { ids } }) : undefined,
  ]);
  return manufacturers ? { ...list, manufacturersById: manufacturers.lookups } : list;
}

/**
 * One result of the island's search, in the list read's item shape, with its
 * whole gallery joined on from beside the page — the donor's search item,
 * which carries `images` (#964 N16). Absent when the item has none.
 */
export type IslandItem = ListPage["items"][number] & {
  images?: NonNullable<ListPage["images"]>[number]["images"];
};

/** A page's items with each one's gallery joined on. */
function pageItems(page: Pick<ListPage, "items" | "images">): IslandItem[] {
  if (!page.images?.length) return page.items;
  const galleries = new Map(page.images.map((entry) => [entry.itemId, entry.images]));
  return page.items.map((item) => {
    const images = galleries.get(item.id);
    return images ? { ...item, images } : item;
  });
}

/** The variant groups the loaded pages carry, for the cards' strips. */
export type IslandVariantGroups = NonNullable<ListPage["variantGroups"]>;

export const itemSearchIslandQueryKeyPrefix = ["item-search-island"] as const;

export function itemSearchIslandQueryKey(search: IslandSearch) {
  const { page: _page, ...query } = islandListInput(search, 1);
  return [...itemSearchIslandQueryKeyPrefix, "search", query] as const;
}

export interface ItemSearchIslandResult {
  /** The query without its page — the scroller remounts when it changes. */
  queryKey: readonly unknown[];
  /**
   * Every loaded page's items, in order — the last answer's when the query has
   * none: a new query's read that fails leaves the previous results.
   */
  items: IslandItem[];
  variantGroups: IslandVariantGroups;
  /** A new query's first page is being read — its results are not shown. */
  isFetchingSearch: boolean;
  isFetchingNextPage: boolean;
  isError: boolean;
  hasMore: boolean;
  /** The first page of the island's first query has never landed. */
  isFirstLoad: boolean;
  fetchNextPage: () => void;
}

/**
 * The island's search — 12 a page, the next page on demand.
 *
 * The donor's `isFetchingSearch` is a new query's read (its results hidden
 * under skeletons) or a next page's (shown beside them). A background re-read
 * of a query already showing is neither here: its results stay as they are.
 *
 * The donor's lazy query hands its grid RTK Query's `data`, the last
 * successful answer (`ItemSearchIsland.tsx:74,100`), so a new query whose read
 * fails shows the previous query's cards under the error snackbar, and `No
 * results` only when there were none (`:272`); `useLastAnswer` keeps it.
 */
export function useItemSearchIsland(search: IslandSearch): ItemSearchIslandResult {
  const queryKey = itemSearchIslandQueryKey(search);
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => searchPage(search, pageParam),
    initialPageParam: 1,
    getNextPageParam: (last: ListPage) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
    staleTime: CATALOG_ITEMS_STALE_TIME_MS,
  });
  // The donor's `isLoading`: true until the hook's first answer, and never
  // again — a later query shows its grid skeletons inside the island instead.
  const [landed, setLanded] = useState(false);
  if (!landed && (query.data !== undefined || query.isError)) setLanded(true);
  const answer = useLastAnswer(query, JSON.stringify(queryKey));
  // A pending query shows nothing of the last one: only a failed one keeps it.
  const pages = query.data !== undefined || query.isError ? answer?.pages : undefined;
  const items = useMemo(() => pages?.flatMap(pageItems) ?? [], [pages]);
  const variantGroups = useMemo(() => {
    const byId = new Map<number, IslandVariantGroups[number]>();
    for (const group of pages?.flatMap((page) => page.variantGroups ?? []) ?? []) {
      byId.set(group.id, group);
    }
    return [...byId.values()];
  }, [pages]);
  return {
    queryKey,
    items,
    variantGroups,
    isFetchingSearch: query.isPending && query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    isError: query.isError,
    hasMore: query.hasNextPage,
    isFirstLoad: !landed && query.data === undefined && !query.isError,
    fetchNextPage: () => void query.fetchNextPage(),
  };
}

/**
 * One item read alone, when the island holds no copy of it — the pinned card
 * (and the last requested one) through the donor's `?itemId=` read: the list
 * read narrowed to one id, so it answers only an item the search could return
 * (not deleted, not hidden), in the card's own shape. No row pins nothing.
 */
export function useIslandSingleItem(itemId: number | null, enabled: boolean) {
  const query = useQuery({
    queryKey: [...itemSearchIslandQueryKeyPrefix, "item", itemId],
    queryFn:
      enabled && itemId !== null
        ? () =>
            catalogItemsList({
              data: {
                page: 1,
                pageSize: 1,
                sort: DEFAULT_CATALOG_SORT,
                itemId,
                includeImages: true,
              },
            })
        : skipToken,
    staleTime: CATALOG_ITEMS_STALE_TIME_MS,
  });
  return {
    item: query.data ? (pageItems(query.data)[0] ?? null) : null,
    variantGroups: query.data?.variantGroups ?? [],
    isFetching: query.isFetching,
    isError: query.isError,
  };
}

/** What the island holds of one item: the item, and its variant group if any. */
interface HeldItem {
  item: IslandItem;
  variantGroups: IslandVariantGroups;
}

type IslandCacheEntry = {
  pages?: ListPage[];
  items?: ListPage["items"];
  images?: ListPage["images"];
  variantGroups?: IslandVariantGroups;
};

/** What one cached island read holds: a search's pages, or a single read. */
function cachedEntryItems(data: IslandCacheEntry | undefined): IslandItem[] {
  return (
    data?.pages?.flatMap(pageItems) ??
    (data?.items ? pageItems({ items: data.items, images: data.images }) : [])
  );
}

type DetailCacheEntry = Awaited<ReturnType<typeof catalogItemDetail>>;

/**
 * What one cached item read holds: the item with its own gallery, and every
 * live member of its variant group with theirs — the donor's `load-item`,
 * which writes the whole group, full details and images, into the object cache
 * (falcon `shared/router.py:416-427`).
 */
function detailEntryItems(data: DetailCacheEntry | undefined): IslandItem[] {
  if (!data) return [];
  const galleries = new Map(
    (data.variantImages ?? []).map((entry) => [entry.itemId, entry.images]),
  );
  if (data.images?.length) galleries.set(data.item.id, data.images);
  return [data.item, ...data.variants.filter(({ id }) => id !== data.item.id)].map((item) => {
    const images = galleries.get(item.id);
    return images ? { ...item, images } : item;
  });
}

/**
 * Every copy of an item the session's cached reads hold — the island's
 * searches' pages and single reads, then every item read (a dialog, a variant
 * preview, an item page) and the variant group it carried. The query client is
 * the island's memory of what has been loaded, as the donor's object cache is
 * (ADR-0014 §2: no store beside TanStack Query), so it outlives the island's
 * mount as the donor's outlives its page. Looked up as the island renders, as
 * `useIslandItem` does.
 */
export function useIslandHeldItems(): IslandItem[] {
  const queryClient = useQueryClient();
  return [
    ...queryClient
      .getQueriesData<IslandCacheEntry>({ queryKey: itemSearchIslandQueryKeyPrefix })
      .flatMap(([, data]) => cachedEntryItems(data)),
    ...queryClient
      .getQueriesData<DetailCacheEntry>({ queryKey: catalogItemDetailQueryKeyPrefix })
      .flatMap(([, data]) => detailEntryItems(data)),
  ];
}

/**
 * The manufacturers the island can name — the donor's object cache of them
 * (#895, #964 N18, #987), read through the query client (ADR-0014 §2) — and
 * nothing else: the menu's live list fills no cache in the donor, so it names
 * no chip. In order, each later source's name winning:
 *
 * - every manufacturer a cached island read's items carry — its searches' and
 *   its single reads', the pinned card's among them;
 * - every manufacturer a cached item read holds, the item's and its variant
 *   group's, as `load-item` writes them all — the item dialog's read;
 * - every manufacturer a cached search page read by id, as falcon's response
 *   merges them last;
 * - the island's picks (`item-search-island-manufacturers`), observed here so
 *   a pick names its chip at once and stays while the island is mounted.
 *
 * Kept as long as those are, across the island's mounts, as the donor's cache
 * outlives its page.
 *
 * Subscribed to those keys in the query cache, as the donor's `useSelector` is
 * to its store: a read the island does not observe — the item dialog's — names
 * its chips the moment it lands.
 */
export function useIslandManufacturerNames(): ReadonlyMap<number, string> {
  const queryClient = useQueryClient();
  // Observed only to hold the picks while the island is mounted.
  useQuery<PickedManufacturer[]>({ queryKey: islandManufacturerPicksQueryKey, queryFn: skipToken });
  const subscribe = useCallback(
    (onChange: () => void) => {
      // Batched as `useQuery`'s own store listener is: a raw listener
      // re-renders synchronously inside the cache's notify, and a query that
      // flips between pending and failed as its observers come and go then
      // never yields.
      const changed = notifyManager.batchCalls(onChange);
      return queryClient.getQueryCache().subscribe(({ query }) => {
        if (NAMING_KEYS.some((prefix) => partialMatchKey(query.queryKey, prefix))) changed();
      });
    },
    [queryClient],
  );
  // A string, so an event that changes no name changes nothing.
  const snapshot = () => JSON.stringify([...cachedManufacturerNames(queryClient)]);
  const names = useSyncExternalStore(subscribe, snapshot, snapshot);
  return useMemo(() => new Map(JSON.parse(names) as [number, string][]), [names]);
}

/** The keys whose answers name the chips. */
const NAMING_KEYS = [
  itemSearchIslandQueryKeyPrefix,
  catalogItemDetailQueryKeyPrefix,
  islandManufacturerPicksQueryKey,
] as const;

function cachedManufacturerNames(queryClient: QueryClient): Map<number, string> {
  const entries = queryClient
    .getQueriesData<IslandCacheEntry>({ queryKey: itemSearchIslandQueryKeyPrefix })
    .map(([, data]) => data);
  const details = queryClient
    .getQueriesData<DetailCacheEntry>({ queryKey: catalogItemDetailQueryKeyPrefix })
    .flatMap(([, data]) => detailEntryItems(data));
  const names = new Map<number, string>();
  for (const item of [...entries.flatMap(cachedEntryItems), ...details]) {
    names.set(item.manufacturer.id, item.manufacturer.name);
  }
  for (const page of entries.flatMap((data) => data?.pages ?? [])) {
    for (const row of page.manufacturersById ?? []) names.set(row.id, row.name);
  }
  const picks = queryClient.getQueryData<PickedManufacturer[]>(islandManufacturerPicksQueryKey);
  for (const row of picks ?? []) names.set(row.id, row.name);
  return names;
}

function heldItem(
  items: readonly IslandItem[],
  groups: IslandVariantGroups,
  id: number,
): HeldItem | null {
  const item = items.find((candidate) => candidate.id === id);
  if (!item) return null;
  const group = groups.find((candidate) => candidate.id === item.variantGroupId);
  return { item, variantGroups: group ? [group] : [] };
}

/**
 * An item for a card the island draws apart from its results — the pinned
 * one, the last requested one: from the current results, else from any query
 * or single read the island has cached (the donor's object cache), else read
 * alone. Its variant group travels with it from wherever it was found, so its
 * strip draws whichever read holds it.
 *
 * The cache is looked up as the island renders, not subscribed to: the island
 * re-renders on its own reads, and an item that leaves the cache falls back to
 * the single read on the next.
 */
export function useIslandItem(itemId: number | null, search: ItemSearchIslandResult) {
  const queryClient = useQueryClient();
  let held: HeldItem | null = null;
  if (itemId !== null) {
    held = heldItem(search.items, search.variantGroups, itemId);
    const cached = held
      ? []
      : queryClient.getQueriesData<IslandCacheEntry>({ queryKey: itemSearchIslandQueryKeyPrefix });
    for (const [, data] of cached) {
      held = heldItem(
        cachedEntryItems(data),
        data?.pages?.flatMap((page) => page.variantGroups ?? []) ?? data?.variantGroups ?? [],
        itemId,
      );
      if (held) break;
    }
  }
  const read = useIslandSingleItem(itemId, held === null);
  if (held) return { ...held, isFetching: false, isError: false };
  return {
    item: read.item?.id === itemId ? read.item : null,
    variantGroups: read.variantGroups,
    isFetching: read.isFetching,
    isError: read.isError,
  };
}
